import React from 'react';
import { Box } from '@mui/material';
import { DragDropContext, Droppable, DropResult } from '@hello-pangea/dnd';
import { ClientField } from '../../../../shared/types/appTypes';
import FormFieldItem from './FormFieldItem';

interface FormFieldListProps {
  fields: ClientField[];
  templateId: string;
  onReorder: (startIndex: number, endIndex: number) => void;
  onReorderChoices: (fieldIndex: number, startIndex: number, endIndex: number) => void;
  onAddChoice: (fieldIndex: number) => void;
  onUpdateChoice: (fieldIndex: number, choiceIndex: number, value: string) => void;
  onDeleteChoice: (fieldIndex: number, choiceIndex: number) => void;
  onUpdate: (index: number, updates: Partial<ClientField>) => void;
  onDelete: (index: number) => void;
}

const FormFieldList: React.FC<FormFieldListProps> = ({
  fields,
  templateId,
  onReorder,
  onReorderChoices,
  onAddChoice,
  onUpdateChoice,
  onDeleteChoice,
  onUpdate,
  onDelete,
}) => {
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    if (result.source.droppableId === 'form-fields' && result.destination.droppableId === 'form-fields') {
      onReorder(result.source.index, result.destination.index);
      return;
    }

    const sourceMatch = result.source.droppableId.match(/^multiple-choice-options-(\d+)$/);
    const destinationMatch = result.destination.droppableId.match(/^multiple-choice-options-(\d+)$/);
    if (!sourceMatch || !destinationMatch) return;
    if (sourceMatch[1] !== destinationMatch[1]) return;

    const fieldIndex = Number(sourceMatch[1]);
    onReorderChoices(fieldIndex, result.source.index, result.destination.index);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="form-fields">
        {(provided) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{ minHeight: 100 }}
          >
            {fields.map((field, index) => (
              <FormFieldItem
                key={`field-${index}`}
                field={field}
                index={index}
                templateId={templateId}
                onUpdate={onUpdate}
                onDelete={onDelete}
                onAddChoice={onAddChoice}
                onUpdateChoice={onUpdateChoice}
                onDeleteChoice={onDeleteChoice}
              />
            ))}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default FormFieldList;
